import { useMemo } from 'react';
import { ArrowRight, BarChart3, BookOpen, CheckCircle2, FileText, PenTool, Sparkles, Target, User } from 'lucide-react';
import { useAppStore } from '../store';
import { ERROR_CATEGORY_COLORS, ERROR_CATEGORY_LABELS } from '../types';
import type { ErrorCategory } from '../types';

const RADAR_CATEGORIES: ErrorCategory[] = [
  'subject-verb-agreement',
  'tense',
  'article',
  'preposition',
  'clause-structure',
];

function polarPoint(cx: number, cy: number, radius: number, index: number, total: number) {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  return {
    x: cx + Math.cos(angle) * radius,
    y: cy + Math.sin(angle) * radius,
  };
}

function RadarChart({ values }: { values: number[] }) {
  const size = 260;
  const center = size / 2;
  const maxRadius = 88;
  const levels = [0.25, 0.5, 0.75, 1];
  const normalizedMax = Math.max(...values, 1);
  const normalized = values.map((v) => v / normalizedMax);

  const dataPolygon = normalized
    .map((value, index) => {
      const p = polarPoint(center, center, maxRadius * value, index, values.length);
      return `${p.x},${p.y}`;
    })
    .join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[320px] mx-auto">
      {levels.map((level) => (
        <polygon
          key={level}
          points={values
            .map((_, index) => {
              const p = polarPoint(center, center, maxRadius * level, index, values.length);
              return `${p.x},${p.y}`;
            })
            .join(' ')}
          fill="none"
          stroke="#d1d5db"
          strokeWidth="1"
        />
      ))}

      {values.map((_, index) => {
        const p = polarPoint(center, center, maxRadius, index, values.length);
        return <line key={index} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#d1d5db" strokeWidth="1" />;
      })}

      <polygon points={dataPolygon} fill="rgba(5, 150, 105, 0.2)" stroke="#059669" strokeWidth="2" />
      {normalized.map((value, index) => {
        const p = polarPoint(center, center, maxRadius * value, index, values.length);
        return <circle key={index} cx={p.x} cy={p.y} r="3" fill="#059669" />;
      })}

      {RADAR_CATEGORIES.map((category, index) => {
        const p = polarPoint(center, center, maxRadius + 22, index, RADAR_CATEGORIES.length);
        return (
          <text
            key={category}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="#475569"
            fontWeight="600"
          >
            {ERROR_CATEGORY_LABELS[category]}
          </text>
        );
      })}
    </svg>
  );
}

export default function LandingPage() {
  const { setActiveTab, essays, favorites, user, startDuePractice } = useAppStore();
  const sampleCorrections = [
    {
      wrong: 'Many student enjoys play basketball after class.',
      correct: 'Many students enjoy playing basketball after class.',
      note: '主谓一致 + 动名词用法',
    },
    {
      wrong: 'Yesterday I go to library and readed two article.',
      correct: 'Yesterday I went to the library and read two articles.',
      note: '时态 + 冠词 + 名词单复数',
    },
    {
      wrong: 'Although he was tired but he still finished homework.',
      correct: 'Although he was tired, he still finished his homework.',
      note: '从句结构 + 标点',
    },
  ];

  const allErrors = useMemo(() => essays.flatMap((essay) => essay.errors), [essays]);

  const errorCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    allErrors.forEach((error) => {
      counts[error.category] = (counts[error.category] || 0) + 1;
    });
    return counts;
  }, [allErrors]);

  const radarValues = useMemo(
    () => RADAR_CATEGORIES.map((category) => errorCountByCategory[category] || 0),
    [errorCountByCategory]
  );

  const topCategories = useMemo(
    () =>
      Object.entries(errorCountByCategory)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3) as Array<[ErrorCategory, number]>,
    [errorCountByCategory]
  );

  const totalWords = useMemo(
    () => essays.reduce((sum, essay) => sum + essay.content.split(/\s+/).filter(Boolean).length, 0),
    [essays]
  );

  const errorDensity = totalWords > 0 ? ((allErrors.length / totalWords) * 100).toFixed(1) : '0.0';
  const dueReviewCount = useMemo(
    () =>
      favorites.filter((favorite) => {
        if (favorite.isMastered || !favorite.nextReviewAt) return false;
        const dueAt = Date.parse(favorite.nextReviewAt);
        return Number.isFinite(dueAt) && dueAt <= Date.now();
      }).length,
    [favorites]
  );
  const masteredCount = useMemo(
    () => favorites.filter((favorite) => favorite.isMastered).length,
    [favorites]
  );
  const practiceButtonLabel = dueReviewCount > 0 ? '开始今日复习' : '查看错题集';

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <section className="relative px-6 pt-12 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(16,185,129,0.15),transparent_35%),radial-gradient(circle_at_88%_8%,rgba(245,158,11,0.14),transparent_28%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
          <div className="relative max-w-6xl mx-auto">
            <div className="rounded-3xl border border-emerald-100 bg-white/90 p-8 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold mb-5">
                <Sparkles size={13} />
                Refine 预览
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight mb-3">
                写错也没关系。
                <br />
                <span className="text-emerald-700 italic">改对才算真正学会。</span>
              </h1>
              <p className="text-slate-600 max-w-2xl mb-8">
                登录后可解锁你的个人错误雷达图、专项练习和错题趋势。先看看系统如何把错误句子改成高分表达。
              </p>
              <button
                onClick={() => setActiveTab('auth')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
              >
                <User size={16} />
                注册后解锁
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </section>

        <section className="px-6 pb-14">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-serif text-3xl text-slate-900 mb-3">修改句子范例</h2>
            <p className="text-slate-500 mb-6">登录后你会在这里看到自己的真实错误和改写结果。</p>
            <div className="grid gap-4">
              {sampleCorrections.map((item) => (
                <div key={item.wrong} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                      <div className="text-xs font-bold text-red-400 mb-1.5 tracking-wider">原句</div>
                      <p className="text-sm text-red-800 leading-relaxed">{item.wrong}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                      <div className="text-xs font-bold text-emerald-400 mb-1.5 tracking-wider">修改后</div>
                      <p className="text-sm text-emerald-800 leading-relaxed font-medium">{item.correct}</p>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500">{item.note}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative px-6 pt-10 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(16,185,129,0.15),transparent_35%),radial-gradient(circle_at_85%_10%,rgba(234,179,8,0.18),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 items-stretch">
            <div className="rounded-3xl border border-emerald-100 bg-white/90 backdrop-blur-sm p-8 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold mb-5">
                <Sparkles size={13} />
                Refine 仪表盘
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight mb-3">
                {user ? '欢迎回来。' : '写错也没关系。'}
                <br />
                <span className="text-emerald-700 italic">{user ? '今天先改最关键的。' : '改对才算真正学会。'}</span>
              </h1>
              <p className="text-slate-600 max-w-xl mb-8">
                {user
                  ? '首页直接看你的薄弱点、今日练习目标和最近分析结果。先做高频错误，提分更快。'
                  : '登录后即可看到你的错误类型雷达图、高频问题和练习建议。'}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setActiveTab('upload')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
                >
                  <FileText size={16} />
                  开始新分析
                </button>
                <button
                  onClick={() => {
                    if (!user) {
                      setActiveTab('auth');
                      return;
                    }
                    if (dueReviewCount > 0) {
                      startDuePractice();
                      return;
                    }
                    setActiveTab('collection');
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
                >
                  {user ? <PenTool size={16} /> : <User size={16} />}
                  {user ? practiceButtonLabel : '登录后解锁'}
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 lg:grid-cols-1 gap-4">
              <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
                <div className="text-xs text-slate-500 mb-1">已分析作文</div>
                <div className="font-serif text-3xl text-slate-900">{essays.length}</div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
                <div className="text-xs text-slate-500 mb-1">每 100 词错误数</div>
                <div className="font-serif text-3xl text-emerald-700">{errorDensity}</div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
                <div className="text-xs text-slate-500 mb-1">今日复习目标</div>
                <div className="font-serif text-3xl text-amber-600">{dueReviewCount}</div>
                <div className="text-xs text-slate-400 mt-1">
                  已掌握 {masteredCount} 条
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-14">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1fr] gap-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-slate-900 flex items-center gap-2">
                <Target size={20} className="text-emerald-600" />
                错误类型雷达图
              </h2>
              <span className="text-xs text-slate-400">基于全部已分析作文</span>
            </div>
            {allErrors.length > 0 ? (
              <>
                <RadarChart values={radarValues} />
                <p className="text-sm text-slate-500 text-center mt-3">
                  样本 {allErrors.length} 条错误，建议优先练习雷达图外圈的维度。
                </p>
              </>
            ) : (
              <div className="py-14 text-center text-slate-400">
                <BarChart3 size={28} className="mx-auto mb-3" />
                上传第一篇作文后，这里会生成你的错误雷达图。
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
            <h2 className="font-serif text-2xl text-slate-900 flex items-center gap-2 mb-5">
              <CheckCircle2 size={20} className="text-amber-500" />
              高频薄弱点
            </h2>
            {topCategories.length > 0 ? (
              <div className="space-y-4">
                {topCategories.map(([category, count]) => {
                  const max = topCategories[0][1] || 1;
                  const width = Math.max(16, Math.round((count / max) * 100));
                  return (
                    <button
                      key={category}
                      onClick={() => setActiveTab('analysis')}
                      className="w-full text-left p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="font-semibold text-slate-700">{ERROR_CATEGORY_LABELS[category]}</span>
                        <span className="text-slate-500">{count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${width}%`, backgroundColor: ERROR_CATEGORY_COLORS[category] }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="py-14 text-center text-slate-400">
                <BookOpen size={28} className="mx-auto mb-3" />
                先分析一篇作文，这里会显示最高频的错误类型。
              </div>
            )}
            <button
              onClick={() => setActiveTab('practice')}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              <PenTool size={16} />
              练习高频错误
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
